import { Link } from "react-router-dom";
import { ArrowLeft, Store, MapPin, Clock, Shield } from "lucide-react";

export default function RetailShopfrontsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[var(--secondary)] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Centre
          </Link>
          <h1 className="text-4xl font-bold">Retail & Shopfronts</h1>
          <p className="text-gray-200 mt-2">
            Everything you need to know about booking and hosting retail spaces
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        {/* Overview */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <div className="flex items-start gap-4 mb-6">
            <Store className="w-8 h-8 text-[var(--secondary)] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-3xl font-bold mb-4">
                Retail Space Overview
              </h2>
              <p className="text-gray-700 leading-relaxed">
                AirArrange makes it easy to find short-term and long-term retail
                spaces perfect for pop-up shops, seasonal stores, product
                launches, or permanent retail locations. Whether you're testing
                a new market or establishing a flagship store, our platform
                connects you with quality retail spaces.
              </p>
            </div>
          </div>
        </section>

        {/* For Tenants */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <h2 className="text-3xl font-bold mb-6">For Retail Tenants</h2>

          <div className="space-y-8">
            {/* Finding the Right Space */}
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-blue-600" />
                Finding the Right Location
              </h3>
              <p className="text-gray-700 mb-3">
                Location is everything in retail. Use our advanced filters to
                find the perfect spot for your business.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Filter by foot traffic levels and pedestrian counts</li>
                <li>Search by proximity to public transportation</li>
                <li>View nearby businesses and complementary retailers</li>
                <li>Check neighborhood demographics and income levels</li>
                <li>Analyze competitor presence in the area</li>
                <li>Review parking availability for customers</li>
              </ul>
            </div>

            {/* Understanding Retail Spaces */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Types of Retail Spaces Available
              </h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Pop-Up Retail Spaces
                  </h4>
                  <p className="text-gray-600">
                    Perfect for short-term promotions, seasonal sales, or market
                    testing. Typically available from a few days to several
                    months with flexible terms.
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Kiosk & Cart Spaces
                  </h4>
                  <p className="text-gray-600">
                    Smaller retail units in high-traffic areas like shopping
                    centers or markets. Ideal for specialized products or food
                    vendors.
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Traditional Storefronts
                  </h4>
                  <p className="text-gray-600">
                    Full retail spaces with street-level access, display
                    windows, and dedicated entrances. Suitable for established
                    brands or long-term leases.
                  </p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">Shared Retail</h4>
                  <p className="text-gray-600">
                    Collaborative spaces where multiple retailers share costs
                    and space. Great for complementary businesses or brand
                    partnerships.
                  </p>
                </div>
              </div>
            </div>

            {/* Lease Considerations */}
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-blue-600" />
                Lease Terms & Considerations
              </h3>
              <p className="text-gray-700 mb-3">
                Understanding your lease agreement is crucial for retail success.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  <strong>Base Rent:</strong> Fixed monthly rent amount for the
                  space
                </li>
                <li>
                  <strong>Common Area Maintenance (CAM):</strong> Your share of
                  building maintenance costs
                </li>
                <li>
                  <strong>Percentage Rent:</strong> Some landlords may require a
                  percentage of sales above a threshold
                </li>
                <li>
                  <strong>Operating Hours:</strong> Verify permitted business
                  hours and any restrictions
                </li>
                <li>
                  <strong>Signage Rights:</strong> Understand what signage
                  you're allowed to display
                </li>
                <li>
                  <strong>Utilities:</strong> Clarify which utilities are
                  included vs. separately metered
                </li>
                <li>
                  <strong>Build-Out Allowances:</strong> Negotiate who pays for
                  renovations and improvements
                </li>
              </ul>
            </div>

            {/* Setting Up Your Space */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Setting Up Your Retail Space
              </h3>
              <p className="text-gray-700 mb-3">
                Checklist for opening your retail location:
              </p>
              <div className="bg-blue-50 rounded-lg p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="business-license"
                    />
                    <label
                      htmlFor="business-license"
                      className="text-gray-700"
                    >
                      Obtain necessary business licenses and permits
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="insurance" />
                    <label htmlFor="insurance" className="text-gray-700">
                      Secure retail liability and property insurance
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="pos" />
                    <label htmlFor="pos" className="text-gray-700">
                      Install point-of-sale (POS) systems and payment processing
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="security" />
                    <label htmlFor="security" className="text-gray-700">
                      Set up security systems and surveillance cameras
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="inventory" />
                    <label htmlFor="inventory" className="text-gray-700">
                      Arrange inventory storage and management systems
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="display" />
                    <label htmlFor="display" className="text-gray-700">
                      Design and install product displays and fixtures
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="signage" />
                    <label htmlFor="signage" className="text-gray-700">
                      Create and install approved signage
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="staff" />
                    <label htmlFor="staff" className="text-gray-700">
                      Hire and train retail staff
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* For Hosts */}
        <section className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold mb-6">For Retail Space Hosts</h2>

          <div className="space-y-8">
            {/* Preparing Your Space */}
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-green-600" />
                Preparing Your Retail Space
              </h3>
              <p className="text-gray-700 mb-3">
                Make your retail space attractive to potential tenants.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  Ensure the space meets commercial building codes and safety
                  requirements
                </li>
                <li>
                  Provide clear floor plans with dimensions and load capacity
                </li>
                <li>Document electrical capacity and outlet locations</li>
                <li>
                  Clean and repair any damage to floors, walls, and fixtures
                </li>
                <li>
                  Ensure HVAC systems are functional and adequately sized
                </li>
                <li>Verify proper lighting for retail display purposes</li>
                <li>
                  Confirm accessibility compliance (ADA in US, similar standards
                  elsewhere)
                </li>
                <li>
                  Provide information about loading dock access or delivery
                  areas
                </li>
              </ul>
            </div>

            {/* Pricing Your Space */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Pricing Your Retail Space
              </h3>
              <p className="text-gray-700 mb-3">
                Factors to consider when setting your rental rate:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Location Factors</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Foot traffic volume</li>
                    <li>• Neighborhood prestige</li>
                    <li>• Proximity to transit</li>
                    <li>• Parking availability</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Space Features</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Square footage</li>
                    <li>• Window displays</li>
                    <li>• Storage space included</li>
                    <li>• Recent renovations</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Amenities</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• WiFi and tech infrastructure</li>
                    <li>• Security systems</li>
                    <li>• Restroom facilities</li>
                    <li>• Climate control</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Market Conditions</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Local competition</li>
                    <li>• Seasonal demand</li>
                    <li>• Economic conditions</li>
                    <li>• Comparable listings</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Legal Requirements */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Legal Requirements for Hosting
              </h3>
              <p className="text-gray-700 mb-3">
                Ensure you're compliant with all regulations:
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> Requirements vary by location.
                  Consult with a local attorney or commercial real estate expert
                  to ensure full compliance.
                </p>
              </div>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  Verify your property is zoned for commercial retail use
                </li>
                <li>
                  Obtain a certificate of occupancy for commercial purposes
                </li>
                <li>
                  Ensure adequate insurance coverage for commercial tenants
                </li>
                <li>
                  Comply with fire safety regulations and inspection requirements
                </li>
                <li>Meet ADA accessibility standards where applicable</li>
                <li>
                  Understand landlord responsibilities for maintenance and repairs
                </li>
                <li>
                  Have a commercial lease agreement reviewed by legal counsel
                </li>
              </ul>
            </div>

            {/* Best Practices */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Best Practices for Retail Hosts
              </h3>
              <div className="space-y-3">
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">
                    ✓ Screen Tenants Carefully
                  </h4>
                  <p className="text-sm text-green-800">
                    Review business plans, financial history, and references to
                    ensure tenants can succeed in your space.
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">
                    ✓ Maintain Open Communication
                  </h4>
                  <p className="text-sm text-green-800">
                    Be responsive to tenant needs and address maintenance issues
                    promptly to maintain good relationships.
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">
                    ✓ Document Everything
                  </h4>
                  <p className="text-sm text-green-800">
                    Keep detailed records of condition reports, communications,
                    and any modifications to the space.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
